import { useStripe } from '@stripe/react-stripe-js'
import { addDays } from 'date-fns'

import { isValidPhoneNumber } from 'react-phone-number-input'

import { emailPatternMatch, getAmountDue } from '../../../utilities/helpers'

import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'

import { Spinner } from '../../../components/LoadingContainer/Spinner'
import { Pricing } from '../Pricing'
import { AssignTo } from '../AssignTo'

export default function ModalForm({
  selectedTickets,
  price,
  setPrice,
  priceTiers,
  paymentMethod,
  setPaymentMethod,
  pricingObject,
  isComplimentary,
  assignTo,
  handleAssignToChange,
  isValidEmail,
  setIsValidEmail,
  isValidNumber,
  setIsValidNumber,
  ticketType,
  buyerTypes,
  selectedBuyerType,
  setSelectedBuyerType,
  compLabels,
  selectedCompLabel,
  setSelectedCompLabel,
  clientSecret,
  initialClientSecret,
  setCard,
  setStripe,
  userType,
  setUserType,
  setInitialExistingUser,
  existingUser,
  setExistingUser,
  installmentMonths,
  setInstallmentMonths,
  paymentPlans,
  setPaymentDate,
  paymentSchedule,
  setPaymentSchedule,
  loadExistingUsers,
  setIsCardValid,
  savedCards,
  isLoadingCards,
  selectedCard,
  setSelectedCard,
  alert,
  closeModal,
  isValid,
  isSaving,
  setIsSaving,
  getText,
  isPackage,
  createOrder,
  card,
  openLoading,
  openError,
  showPaymentSuccess,
  showAssignmentSuccess,
  openMarkAsComplete,
  openDelete,
  processTerminalPayment,
  createPaymentPlanOrder,
  chargePaymentMethod,
  eventPackage,
  dueDateOpt,
  setDueDateOpt,
  customDueDate,
  setCustomDueDate,
  showRemoveSeat,
  onRemoveSeat,
  organizationUUID,
}) {
  const stripe = useStripe()

  async function handleCardPayment(hasPaymentPlan) {
    setIsSaving(true)

    try {
      // Ensure Stripe is loaded before proceeding
      if (!stripe) {
        throw new Error(
          'Stripe is not initialized. Please refresh the page and try again.'
        )
      }

      // Ensure we have a client secret
      if (!clientSecret) {
        throw new Error('Payment intent is missing. Please try again.')
      }

      let paymentIntentId

      if (!hasPaymentPlan && selectedCard) {
        const pmid = await chargePaymentMethod()
        if (pmid?.error) throw new Error(pmid.error.message)
        paymentIntentId = pmid?.data.id
      } else {
        // Ensure we have a card element or selected card
        if (!selectedCard && !card) {
          throw new Error('Please enter card details or select a saved card.')
        }

        const payload = await stripe.confirmCardPayment(clientSecret, {
          payment_method: selectedCard ? selectedCard : { card },
        })
        if (payload?.error) throw new Error(payload.error.message)
        paymentIntentId = payload?.paymentIntent?.id
      }

      hasPaymentPlan
        ? createPaymentPlanOrder(paymentIntentId, showPaymentSuccess)
        : createOrder(paymentIntentId, showPaymentSuccess)
    } catch (err) {
      setIsSaving(false)
      // Provide more helpful error messages
      let errorMessage = err.message

      // Check for API key mismatch errors
      if (err.message && err.message.includes('Invalid API Key')) {
        errorMessage =
          'There is a configuration issue with the payment system. Please contact support.'
      }

      openError(
        'Payment failed!',
        `Payment could not be processed. ${errorMessage} Please try again.`
      )
    }
  }

  const handleSave = async (e) => {
    const hasPaymentPlan = Object.keys(paymentPlans).length > 0

    e.preventDefault()

    const isValidEmail = (email) => {
      if (!emailPatternMatch(email)) {
        return false
      } else {
        return true
      }
    }

    const isValidNumber = (number) => {
      // Since not required -> empty is valid
      if (!number) {
        return true
      }

      return isValidPhoneNumber(number)
    }

    // Validate both steps before proceeding
    if (
      isValid &&
      isValidEmail(assignTo.email) &&
      isValidNumber(assignTo.phoneNumber)
    ) {
      switch (paymentMethod) {
        case 'INVOICE':
          setIsSaving(true)

          let dueDate
          if (dueDateOpt === 'custom') {
            dueDate = customDueDate
          } else {
            // tomorrow, 7, 14 etc.
            if (dueDateOpt > 0) {
              dueDate = addDays(new Date(), dueDateOpt)
            } else {
              // today
              dueDate = new Date()
            }
          }
          if (!hasPaymentPlan) {
            createOrder('', showAssignmentSuccess, dueDate?.toISOString())
          }
          break

        case 'POS':
          if (hasPaymentPlan) break

          openLoading(
            'POS is awaiting payment...',
            'card',
            'Payment in progress'
          )

          let terminalPayment
          try {
            terminalPayment = await processTerminalPayment()

            // success path
            console.log('Payment succeeded', terminalPayment)
          } catch (error) {
            openError(
              'Payment failed!',
              "The connection to POS wasn't successful. Please make sure it is connected properly and try again."
            )
            console.error('Terminal payment failed:', error)
            return
          }

          createOrder(terminalPayment?.paymentIntent?.id, showPaymentSuccess)
          break

        case 'CARD':
          handleCardPayment(hasPaymentPlan)
          break

        default:
          const allowed = ['COMP', 'RESERVED']
          if (
            allowed.includes(paymentMethod) ||
            getAmountDue(
              pricingObject,
              paymentMethod,
              hasPaymentPlan,
              paymentPlans
            ) === 0
          ) {
            setIsSaving(true)
            createOrder('', showAssignmentSuccess)
            return
          }
          openMarkAsComplete(paymentMethod)
          break
      }
    }
  }

  const getHeading = () => {
    return {
      title: `Assign ${isPackage ? 'package' : 'tickets'}`,
      desc: "Choose the options that meet the user's requirements and provide buyer information.",
    }
  }

  const heading = getHeading()

  return (
    <>
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">{heading?.title}</h1>
        <p className="mt-0 normal">{heading?.desc}</p>
      </div>
      <Form onSubmit={handleSave} className="d-flex-column flex-grow-1">
        <Stack gap={4}>
          {/* Show Pricing component when not complimentary */}
          {!isComplimentary && (
            <Pricing
              selectedTickets={selectedTickets}
              isPackage={isPackage}
              showRemoveSeat={showRemoveSeat}
              onRemoveSeat={onRemoveSeat}
              ticketType={ticketType}
              price={price}
              setPrice={setPrice}
              priceTiers={priceTiers}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              allowEditPrice={true}
              pricingObject={pricingObject}
              paymentSchedule={paymentSchedule}
              setPaymentSchedule={setPaymentSchedule}
              installmentMonths={installmentMonths}
              setInstallmentMonths={setInstallmentMonths}
              paymentPlans={paymentPlans}
              setPaymentDate={setPaymentDate}
              eventPackage={eventPackage}
              dueDateOpt={dueDateOpt}
              setDueDateOpt={setDueDateOpt}
              customDueDate={customDueDate}
              setCustomDueDate={setCustomDueDate}
              organizationUUID={organizationUUID}
            />
          )}
          {/* Always show AssignTo component */}
          <AssignTo
            amountToPay={pricingObject?.purchasePriceWithTaxAndFees}
            selectedTickets={selectedTickets}
            showRemoveSeat={showRemoveSeat}
            onRemoveSeat={onRemoveSeat}
            assignTo={assignTo}
            onAssignToChange={handleAssignToChange}
            isValidEmail={isValidEmail}
            setIsValidEmail={setIsValidEmail}
            isValidNumber={isValidNumber}
            setIsValidNumber={setIsValidNumber}
            buyerTypes={buyerTypes}
            selectedBuyerType={selectedBuyerType}
            compLabels={compLabels}
            selectedCompLabel={selectedCompLabel}
            setSelectedBuyerType={setSelectedBuyerType}
            setSelectedCompLabel={setSelectedCompLabel}
            clientSecret={clientSecret}
            initialClientSecret={initialClientSecret}
            setCard={setCard}
            setStripe={setStripe}
            userType={userType}
            setUserType={setUserType}
            setInitialExistingUser={setInitialExistingUser}
            existingUser={existingUser}
            setExistingUser={setExistingUser}
            loadExistingUsers={loadExistingUsers}
            setIsCardValid={setIsCardValid}
            savedCards={savedCards}
            isLoadingCards={isLoadingCards}
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
            paymentMethod={paymentMethod}
            openDelete={openDelete}
            paymentSchedule={paymentSchedule}
            installmentMonths={installmentMonths}
            paymentPlans={paymentPlans}
            pricingObject={pricingObject}
          />
        </Stack>
        {alert.show && (
          <Alert variant={alert.variant} className="mt-4">
            <p>{alert.message}</p>
          </Alert>
        )}
        <Stack
          direction="horizontal"
          className="btn-group-flex justify-content-end"
        >
          <Stack direction="horizontal">
            <Button variant="default" size="lg" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              size="lg"
              className="btn-width btn-width-xxl--flex-item"
              disabled={!isValid || isSaving}
              type="submit"
            >
              {isSaving ? <Spinner /> : getText()}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </>
  )
}
